// Story 10.1/10.5 — App root: providers (Query, Router) y boot opcional de
// `@axe-core/react` solo en dev mode para detectar violations a11y.
// Story 10.5: registra el listener global de 401 para redirigir a /login.

import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { Toaster, toast } from 'sonner';
import { onUnauthorized } from '@/lib/api-client';
import { queryClient } from '@/lib/query-client';
import { router } from '@/router';

export function App(): React.ReactElement {
  // P-312: ref-guard para que N queries fallando con 401 en paralelo
  // (cookie expirada → dashboard pide me + bots + analytics + ...)
  // produzcan UN solo toast + UNA sola navegación. El flag se mantiene
  // hasta que el user llega a /login; cualquier 401 posterior dentro de
  // la misma "rafaga" ya no spamea.
  const expiryFiring = useRef(false);

  useEffect(() => {
    return onUnauthorized(() => {
      if (expiryFiring.current) return;
      expiryFiring.current = true;
      // P-316: limpiar TODA la cache (no solo `auth/me`). Datos
      // sensibles — analytics, conversaciones, bots, stats — no deben
      // sobrevivir cuando otro user logue desde el mismo browser.
      queryClient.clear();
      toast.error('Tu sesión expiró. Vuelve a iniciar sesión.');
      // biome-ignore lint/suspicious/noExplicitAny: TanStack search typing relajado.
      void router.navigate({ to: '/login' as any }).finally(() => {
        // Reset tras navegar — la próxima sesión nueva debe poder volver
        // a disparar el flow si la cookie vuelve a expirar.
        expiryFiring.current = false;
      });
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster theme="dark" position="bottom-right" />
    </QueryClientProvider>
  );
}
